import React, {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';
import {
    Badge,
    Card,
    Col,
    Container,
    Row,
} from 'react-bootstrap';
import { NavLink } from 'react-router-dom';
import { FaEdit } from 'react-icons/fa';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';

import SmartTable from '../../components/Smart/SmartTable';
import SmartTopTool from '../../components/Smart/SmartTopTool';
import SmartPagination from '../../components/Smart/SmartPagination';
import API from '../../api/axios';

const SEARCH_TYPE = [
    {
        key: 'brand',
        label: 'Brand',
    },
    {
        key: 'name',
        label: 'Name',
    },
    {
        key: 'id',
        label: 'ID',
    },
];

const FILTER_OPTIONS = {
    type: [
        'SCOOTER',
        'NAKED',
        'TRAIL',
        'SPORT',
        'TOURING',
        'ENDURO',
        'CUSTOM',
        'CLASSIC',
        'CAFE_RACER',
        'SCRAMBLER',
        'ELECTRIC',
        'QUAD',
        'BICYCLE',
        'ELECTRIC_BICYCLE',
        'CAR',
        'SNOWMOBILE',
        'TRIKE',
        'UTV',
    ],
};

const SORT_OPTIONS = [
    'ID',
    'Brand',
    'Name',
    'CreatedAt',
];

const SORT_MAP = {
    ID: 'name',
    Brand: 'brand',
    Name: 'name',
    CreatedAt: 'createdAt',
};

const DEFAULT_META = {
    total: 0,
    page: 1,
    pageSize: 12,
    totalPages: 1,
};

const DEFAULT_PAGE = {
    page_number: 1,
    total_pages: 1,
};

const DEFAULT_FILTERS = {
    type: [],
};

const DEFAULT_SORT = {
    value: null,
    order: 'asc',
};

const normalizeModelRow = (item) => ({
    id: item.id,

    brand:
        item.Brand ??
        item.brand ??
        {
            name: '-',
        },

    name: item.name,

    type:
        item.type ??
        null,
});

const Models = () => {
    const [rows, setRows] =
        useState([]);

    const [meta, setMeta] =
        useState(DEFAULT_META);

    const [loading, setLoading] =
        useState(false);

    const [error, setError] =
        useState('');

    const [
        searchConditions,
        setSearchConditions,
    ] = useState([]);

    const [filters, setFilters] =
        useState(DEFAULT_FILTERS);

    const [sort, setSort] =
        useState(DEFAULT_SORT);

    const [page, setPage] =
        useState(DEFAULT_PAGE);

    const fetchModels =
        useCallback(async () => {
            try {
                setLoading(true);
                setError('');

                const params =
                    new URLSearchParams();

                params.set(
                    'page',
                    String(
                        page.page_number ||
                        1,
                    ),
                );

                const sortBy =
                    sort.value
                        ? SORT_MAP[
                        sort.value
                        ] || 'name'
                        : 'name';

                params.set(
                    'sortBy',
                    sortBy,
                );

                params.set(
                    'order',
                    sort.order ===
                        'desc'
                        ? 'desc'
                        : 'asc',
                );

                searchConditions.forEach(
                    ({
                        type,
                        value,
                    }) => {
                        if (
                            value == null ||
                            String(value).trim() ===
                            ''
                        ) {
                            return;
                        }

                        const normalizedValue =
                            String(
                                value,
                            ).trim();

                        switch (type) {
                            case 'brand':
                                params.set(
                                    'brandSlug',
                                    normalizedValue,
                                );
                                break;

                            case 'name':
                                params.set(
                                    'name',
                                    normalizedValue,
                                );
                                break;

                            case 'id':
                                params.set(
                                    'id',
                                    normalizedValue,
                                );
                                break;

                            default:
                                break;
                        }
                    },
                );

                const selectedType =
                    filters.type?.[0];

                if (selectedType) {
                    params.set(
                        'type',
                        String(
                            selectedType,
                        ).toUpperCase(),
                    );
                }

                const {
                    data,
                } =
                    await API.get(
                        `/motorcycles?${params.toString()}`,
                    );

                const list =
                    Array.isArray(
                        data?.data,
                    )
                        ? data.data
                        : [];

                const pagination =
                    data?.meta ||
                    DEFAULT_META;

                setRows(
                    list.map(
                        normalizeModelRow,
                    ),
                );

                const nextMeta = {
                    total:
                        pagination.total ??
                        0,

                    page:
                        pagination.page ??
                        1,

                    pageSize:
                        pagination.pageSize ??
                        12,

                    totalPages:
                        pagination.totalPages ??
                        1,
                };

                setMeta(nextMeta);

                setPage((prev) => {
                    const nextPage =
                        nextMeta.page ||
                        1;

                    const nextTotalPages =
                        nextMeta.totalPages ||
                        1;

                    if (
                        prev.page_number ===
                        nextPage &&
                        prev.total_pages ===
                        nextTotalPages
                    ) {
                        return prev;
                    }

                    return {
                        ...prev,
                        page_number:
                            nextPage,
                        total_pages:
                            nextTotalPages,
                    };
                });
            } catch (e) {
                setError(
                    e?.response?.data
                        ?.message ||
                    e?.message ||
                    'Failed to load',
                );

                setRows([]);
            } finally {
                setLoading(false);
            }
        }, [
            page.page_number,
            searchConditions,
            filters.type,
            sort.value,
            sort.order,
        ]);

    const handleSetSearchConditions =
        useCallback(
            (valueOrUpdater) => {
                setSearchConditions(
                    (prev) =>
                        typeof valueOrUpdater ===
                            'function'
                            ? valueOrUpdater(
                                prev,
                            )
                            : valueOrUpdater,
                );

                setPage((prev) =>
                    prev.page_number === 1
                        ? prev
                        : {
                            ...prev,
                            page_number: 1,
                        },
                );
            },
            [],
        );

    const handleSetFilters =
        useCallback(
            (valueOrUpdater) => {
                setFilters(
                    (prev) =>
                        typeof valueOrUpdater ===
                            'function'
                            ? valueOrUpdater(
                                prev,
                            )
                            : valueOrUpdater,
                );

                setPage((prev) =>
                    prev.page_number === 1
                        ? prev
                        : {
                            ...prev,
                            page_number: 1,
                        },
                );
            },
            [],
        );

    const handleSetSort =
        useCallback(
            (valueOrUpdater) => {
                setSort(
                    (prev) =>
                        typeof valueOrUpdater ===
                            'function'
                            ? valueOrUpdater(
                                prev,
                            )
                            : valueOrUpdater,
                );

                setPage((prev) =>
                    prev.page_number === 1
                        ? prev
                        : {
                            ...prev,
                            page_number: 1,
                        },
                );
            },
            [],
        );

    useEffect(() => {
        fetchModels();
    }, [fetchModels]);

    const columns = useMemo(
        () => [
            {
                key: 'brand',
                label: 'Brand',
                render: (item) =>
                    item.brand?.name ??
                    item.brand ??
                    '-',
            },
            {
                keys: [
                    'id',
                    'name',
                ],
                label: 'Name',
                render: (item) => (
                    <div className="d-flex align-items-center">
                        <OverlayTrigger
                            placement="top"
                            overlay={
                                <Tooltip>
                                    ID:{' '}
                                    {
                                        item.id
                                    }
                                </Tooltip>
                            }
                        >
                            <span
                                style={{
                                    cursor:
                                        'pointer',
                                }}
                            >
                                {item.name ||
                                    '-'}
                            </span>
                        </OverlayTrigger>
                    </div>
                ),
            },
            {
                key: 'type',
                label: 'Type',
                render: (item) =>
                    item.type ? (
                        <Badge bg="info">
                            {
                                item.type
                            }
                        </Badge>
                    ) : (
                        <span className="text-muted">
                            -
                        </span>
                    ),
            },
            {
                key: 'actions',
                label: 'Actions',
                render: (item) => (
                    <div className="d-flex gap-2 align-items-center">
                        <NavLink
                            to={`/models/edit/${item.id}`}
                            className="btn btn-edit p-0"
                            title="Edit"
                        >
                            <FaEdit
                                size={20}
                                style={{
                                    marginLeft:
                                        '4px',
                                }}
                            />
                        </NavLink>
                    </div>
                ),
            },
        ],
        [],
    );

    return (
        <Container
            fluid
            className="d-flex flex-column"
        >
            <Row className="mb-4">
                <Col sm={8}>
                    <span className="fw-semibold">
                        Models List
                    </span>
                </Col>

                <Col
                    sm={4}
                    className="text-end"
                >
                    <span className="fw-semibold">
                        <NavLink
                            to="/dashboard"
                            className="block-link"
                        >
                            Dashboard
                        </NavLink>{' '}
                        - Models List
                    </span>
                </Col>
            </Row>

            <Row>
                <Col sm={12}>
                    <Card className="h-100 p-2">
                        <Card.Body className="px-2 pt-2 pb-5">
                            <Row>
                                <Col md={10}>
                                    <SmartTopTool
                                        searchType={
                                            SEARCH_TYPE
                                        }
                                        searchConditions={
                                            searchConditions
                                        }
                                        setSearchConditions={
                                            handleSetSearchConditions
                                        }
                                        filterOptions={
                                            FILTER_OPTIONS
                                        }
                                        filters={
                                            filters
                                        }
                                        setFilters={
                                            handleSetFilters
                                        }
                                        sortOptions={
                                            SORT_OPTIONS
                                        }
                                        sort={
                                            sort
                                        }
                                        setSort={
                                            handleSetSort
                                        }
                                    />
                                </Col>

                                <Col
                                    md={2}
                                    className="d-flex justify-content-end"
                                >
                                    <NavLink
                                        to="/models/new"
                                        className="border ms-1 btn-custom-success btn btn-primary"
                                        title="New"
                                        style={{
                                            height:
                                                '40px',
                                        }}
                                    >
                                        + New
                                    </NavLink>
                                </Col>

                                <Col md={12}>
                                    <SmartTable
                                        data={
                                            rows
                                        }
                                        columns={
                                            columns
                                        }
                                        loading={
                                            loading
                                        }
                                        emptyState={
                                            <div className="text-center py-5 text-muted">
                                                <p>
                                                    {error ||
                                                        'No matching Models found'}
                                                </p>
                                            </div>
                                        }
                                    />
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>
                </Col>

                <Col
                    md={12}
                    className="d-flex justify-content-end"
                >
                    <SmartPagination
                        page={page}
                        setPage={setPage}
                    />
                </Col>
            </Row>
        </Container>
    );
};

export default Models;